package dev.terrakok.mapitan

import androidx.compose.foundation.Image
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.key.KeyEvent
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.Clipboard
import androidx.compose.ui.platform.LocalClipboard
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.DpRect
import androidx.compose.ui.unit.dp
import dev.terrakok.mapitan.domain.Area
import dev.terrakok.mapitan.domain.Document
import dev.terrakok.mapitan.domain.DocumentObject
import dev.terrakok.mapitan.domain.DocumentSerialization
import dev.terrakok.mapitan.domain.Label
import dev.terrakok.mapitan.domain.LineStyle
import dev.terrakok.mapitan.domain.Pin
import dev.terrakok.mapitan.domain.Track
import dev.terrakok.mapitan.domain.asClipEntry
import dev.terrakok.mapitan.domain.createProjectDeeplink
import dev.terrakok.mapitan.domain.geojson.*
import dev.terrakok.mapitan.ui.Icons
import dev.terrakok.mapitan.ui.components.*
import kotlinx.coroutines.launch
import org.maplibre.compose.map.MapState
import org.maplibre.compose.map.MapUiOptions
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.spatialk.geojson.Position

@Immutable
data class AppUiState(
    val doc: Document,
    val mode: Mode,
    val selectedColor: Color,
    val selectedLineThickness: Int,
    val selectedLineStyle: LineStyle,
    val labelText: String,
    val selectedObject: DocumentObject?,
    val draggingPin: PinDrag?,
    val draggingLabel: LabelDrag?,
    val trackDraft: TrackDraft?,
    val draggingTrackPoint: TrackPointDrag?,
    val areaDraft: AreaDraft?,
    val draggingAreaPoint: AreaPointDrag?,
    val canUndo: Boolean,
    val canRedo: Boolean,
    val lockIsClosed: Boolean,
)

@Immutable
data class AppCallbacks(
    val onKeyClick: (KeyEvent) -> Boolean,
    val selectMode: (Mode) -> Unit,
    val selectColor: (Color) -> Unit,
    val selectLineThickness: (Int) -> Unit,
    val selectLineStyle: (LineStyle) -> Unit,
    val setLabelText: (String) -> Unit,
    val removeSelectedObject: () -> Unit,
    val undo: () -> Unit,
    val redo: () -> Unit,
    val switchLock: () -> Unit,
    val startDragLabel: (String) -> Boolean,
    val updateDragLabel: (Position) -> Unit,
    val cancelDragLabel: () -> Unit,
    val finishDragLabel: () -> Unit,
    val startDragPin: (String) -> Boolean,
    val updateDragPin: (Position) -> Unit,
    val cancelDragPin: () -> Unit,
    val finishDragPin: () -> Unit,
    val startDragTrackPoint: (String, String) -> Boolean,
    val updateDragTrackPoint: (Position) -> Unit,
    val cancelDragTrackPoint: () -> Unit,
    val finishDragTrackPoint: () -> Unit,
    val removeTrackPoint: (String, String) -> Unit,
    val startDragAreaPoint: (String, String) -> Boolean,
    val updateDragAreaPoint: (Position) -> Unit,
    val cancelDragAreaPoint: () -> Unit,
    val finishDragAreaPoint: () -> Unit,
    val removeAreaPoint: (String, String) -> Unit,
)

@Composable
fun AppContent(
    mapState: MapState,
    uiState: AppUiState,
    callbacks: AppCallbacks,
    mapInteractions: MapInteractions,
    mapUiOptions: MapUiOptions,
    onCursorPositionChange: (Position?) -> Unit,
) {
    val density = LocalDensity.current
    val doc = uiState.doc
    val mode = uiState.mode
    val coroutineScope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }

    // Local UI state
    var cursorOffset by remember { mutableStateOf<Offset?>(null) }

    val isDrafting = uiState.trackDraft != null || uiState.areaDraft != null

    LaunchedEffect(cursorOffset, mapState.cameraPosition, isDrafting) {
        onCursorPositionChange(
            if (isDrafting) {
                cursorOffset?.let { mapState.positionFromScreenLocation(with(density) { toDpOffset(it) }) }
            } else {
                null
            }
        )
    }

    Box(
        modifier = Modifier.onKeyEvent(callbacks.onKeyClick)
    ) {
        MaplibreMap(
            modifier = Modifier.pointerInput(isDrafting) {
                if (!isDrafting) {
                    cursorOffset = null
                    return@pointerInput
                }
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent(PointerEventPass.Initial)
                        cursorOffset = event.changes.lastOrNull()?.position
                    }
                }
            }.pointerInput(callbacks, mapState, mode, doc) {
                if (mode != Mode.Cursor) return@pointerInput
                while (true) {
                    val down = awaitPointerEventScope {
                        awaitFirstDown(requireUnconsumed = false, pass = PointerEventPass.Initial)
                    }
                    val labelId = mapState
                        .queryRenderedFeatures(toDpRect(down.position), setOf(MapKeys.LABEL_LAYER_ID))
                        .findDocumentObjectIds().firstOrNull()
                    if (labelId != null) {
                        val label = doc.findById(labelId) as? Label ?: continue
                        if (!callbacks.startDragLabel(labelId)) continue
                        awaitPointerEventScope {
                            dragAnchoredPoint(
                                density = density,
                                down = down,
                                mapState = mapState,
                                anchor = label.point.coordinates,
                                onDrag = { callbacks.updateDragLabel(it) },
                                onTap = { callbacks.cancelDragLabel() },
                                onFinish = { callbacks.finishDragLabel() },
                                onCancel = { callbacks.cancelDragLabel() },
                            )
                        }
                        continue
                    }

                    val pinId = mapState
                        .queryRenderedFeatures(toDpRect(down.position), setOf(MapKeys.PIN_LAYER_ID))
                        .findDocumentObjectIds().firstOrNull()
                    if (pinId != null) {
                        val pin = doc.findById(pinId) as? Pin ?: continue
                        if (!callbacks.startDragPin(pinId)) continue
                        awaitPointerEventScope {
                            dragAnchoredPoint(
                                density = density,
                                down = down,
                                mapState = mapState,
                                anchor = pin.point.coordinates,
                                onDrag = { callbacks.updateDragPin(it) },
                                onTap = { callbacks.cancelDragPin() },
                                onFinish = { callbacks.finishDragPin() },
                                onCancel = { callbacks.cancelDragPin() },
                            )
                        }
                        continue
                    }

                    val vertex = mapState
                        .queryRenderedFeatures(
                            toDpRect(down.position),
                            setOf(MapKeys.TRACK_POINT_LAYER_ID),
                        )
                        .findTrackPointRef()
                    if (vertex != null) {
                        if (vertex.trackId == MapKeys.DRAFT_TRACK_ID) continue
                        if (vertex.trackId != uiState.selectedObject?.id) continue
                        val track = doc.findById(vertex.trackId) as? Track ?: continue
                        val point = track.points.firstOrNull { it.id == vertex.pointId } ?: continue
                        if (!callbacks.startDragTrackPoint(vertex.trackId, vertex.pointId)) continue
                        awaitPointerEventScope {
                            dragAnchoredPoint(
                                density = density,
                                down = down,
                                mapState = mapState,
                                anchor = point.point.coordinates,
                                onDrag = { callbacks.updateDragTrackPoint(it) },
                                onTap = {
                                    callbacks.cancelDragTrackPoint()
                                    callbacks.removeTrackPoint(vertex.trackId, vertex.pointId)
                                },
                                onFinish = { callbacks.finishDragTrackPoint() },
                                onCancel = { callbacks.cancelDragTrackPoint() },
                            )
                        }
                        continue
                    }

                    val areaVertex = mapState
                        .queryRenderedFeatures(
                            toDpRect(down.position),
                            setOf(MapKeys.AREA_POINT_LAYER_ID),
                        )
                        .findAreaPointRef()
                    if (areaVertex != null) {
                        if (areaVertex.areaId == MapKeys.DRAFT_AREA_ID) continue
                        if (areaVertex.areaId != uiState.selectedObject?.id) continue
                        val area = doc.findById(areaVertex.areaId) as? Area ?: continue
                        val point = area.points.firstOrNull { it.id == areaVertex.pointId } ?: continue
                        if (!callbacks.startDragAreaPoint(areaVertex.areaId, areaVertex.pointId)) continue
                        awaitPointerEventScope {
                            dragAnchoredPoint(
                                density = density,
                                down = down,
                                mapState = mapState,
                                anchor = point.point.coordinates,
                                onDrag = { callbacks.updateDragAreaPoint(it) },
                                onTap = {
                                    callbacks.cancelDragAreaPoint()
                                    callbacks.removeAreaPoint(areaVertex.areaId, areaVertex.pointId)
                                },
                                onFinish = { callbacks.finishDragAreaPoint() },
                                onCancel = { callbacks.cancelDragAreaPoint() },
                            )
                        }
                    }
                }
            },
            state = mapState,
            interactions = mapInteractions,
            uiOptions = mapUiOptions,
        )

        EditorOptions(
            editorMode = mode,
            color = uiState.selectedColor,
            onColorSelected = { callbacks.selectColor(it) },
            lineThickness = uiState.selectedLineThickness,
            onLineThicknessSelected = { callbacks.selectLineThickness(it) },
            lineStyle = uiState.selectedLineStyle,
            onLineStyleSelected = { callbacks.selectLineStyle(it) },
            labelText = uiState.labelText,
            selectedObject = uiState.selectedObject,
            onLabelTextChanged = { callbacks.setLabelText(it) },
            onRemoveSelectedObject = { callbacks.removeSelectedObject() },
            modifier = Modifier.padding(16.dp).padding(top = 60.dp)
        )

        CanvasToolbar(
            scalePercent = (100.0 * mapState.cameraPosition.zoom / 25.5).toInt(),
            canUndo = uiState.canUndo,
            canRedo = uiState.canRedo,
            onZoomOut = {
                coroutineScope.launch {
                    mapState.animateCameraPosition(mapState.cameraPosition.copy(zoom = mapState.cameraPosition.zoom - 1))
                }
            },
            onZoomIn = {
                coroutineScope.launch {
                    mapState.animateCameraPosition(mapState.cameraPosition.copy(zoom = mapState.cameraPosition.zoom + 1))
                }
            },
            onUndo = { callbacks.undo() },
            onRedo = { callbacks.redo() },
            onResetZoom = {
                coroutineScope.launch {
                    mapState.animateCameraPosition(mapState.cameraPosition.copy(zoom = 25.5 * 0.3))
                }
            },
            modifier = Modifier
                .align(Alignment.BottomStart)
                .padding(16.dp),
        )

        ToolBar(
            currentMode = mode,
            onModeChanged = { callbacks.selectMode(it) },
            lockIsClosed = uiState.lockIsClosed,
            onLockClicked = { callbacks.switchLock() },
            modifier = Modifier
                .align(Alignment.TopCenter)
                .padding(top = 16.dp),
        )

        var menuExpanded by remember { mutableStateOf(false) }
        Box(
            modifier = Modifier.padding(16.dp),
        ) {
            Card(
                onClick = { menuExpanded = true },
                modifier = Modifier.size(34.dp),
                shape = MaterialTheme.shapes.small,
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceContainerHighest,
                ),
                elevation = CardDefaults.cardElevation(6.dp),
            ) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    Image(
                        imageVector = Icons.Hamburger,
                        contentDescription = "Menu",
                    )
                }
            }
            DropdownMenu(
                expanded = menuExpanded,
                onDismissRequest = { menuExpanded = false },
            ) {
                val clipboard = LocalClipboard.current
                MainMenuItem(Icons.Open, "Open") {}
                MainMenuItem(Icons.SaveTo, "Download ") {}
                MainMenuItem(Icons.ExportImage, "Print") {}
                MainMenuItem(Icons.Help, "Help") {}
                MainMenuItem(Icons.Reset, "Clear") {}
                MainMenuItem(Icons.Theme, "Theme") {}
                MainMenuItem(Icons.MapStyle, "Style") {}
                MainMenuItem(Icons.ShareUrl, "Share as URL") {
                    menuExpanded = false
                    coroutineScope.launch { copyDocumentToClipbord(clipboard, snackbarHostState, doc) }
                }
            }
        }

        SnackbarHost(
            hostState = snackbarHostState,
            modifier = Modifier.align(Alignment.BottomCenter).padding(16.dp),
        )
    }
}

@Composable
private fun MainMenuItem(
    icon: ImageVector,
    text: String,
    onClick: () -> Unit,
) {
    DropdownMenuItem(
        contentPadding = PaddingValues(8.dp),
        modifier = Modifier.height(40.dp),
        leadingIcon = { Icon(icon, contentDescription = null, modifier = Modifier.size(24.dp)) },
        text = { Text(text, style = MaterialTheme.typography.labelMedium) },
        onClick = onClick,
    )
}

private suspend fun copyDocumentToClipbord(
    clipboard: Clipboard,
    snackbarHostState: SnackbarHostState,
    document: Document
) {
    runCatching {
        val deeplink = createProjectDeeplink(
            DocumentSerialization.encode(document)
        )
        clipboard.setClipEntry(deeplink.asClipEntry())
    }.onSuccess {
        snackbarHostState.showSnackbar("Project URL copied")
    }.onFailure {
        snackbarHostState.showSnackbar("Couldn't copy project URL")
    }
}

private fun Density.toDpOffset(offset: Offset): DpOffset = DpOffset(offset.x.toDp(), offset.y.toDp())
private fun Density.toDpRect(offset: Offset): DpRect =
    DpRect(offset.x.toDp(), offset.y.toDp(), offset.x.toDp(), offset.y.toDp())
